import { useState } from "react"
import {
  useGetMetaAccountUsersQuery,
  useLazyGetMetaAccountUserOauthUrlQuery,
  useRemoveMetaAccountUserMutation,
  useSetMetaAccountUserDiscoveryDefaultMutation,
  useSetMetaAccountUserPausedMutation,
} from "~/ui-rtk/api/metaAccountApi"
import type { MetaAccountUser } from "~/ui-rtk/api/types/meta-account.types"
// Direct imports (not the `common` barrel) — see CreativesTab.tsx.
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"
import { markMetaAccountLoginStarted } from "~/ui-rtk/utils/meta-account-login-marker"
import AccessChip from "./AccessChip"
import { DIALOG_CONTAINER_CLS } from "./AssignMetaAccessDialog"
import {
  BUTTON,
  CHANGE_FAILED,
  CONNECT_HELP,
  CONNECT_LABEL,
  CONNECT_START_FAILED,
  CONNECT_WARNING,
  COVERAGE_TITLE,
  LOGIN_ACTION,
  LOGIN_CHIP,
  LOGIN_ROW_LABEL,
  MANAGE_EMPTY,
  MANAGE_HELP,
  MANAGE_LOAD_ERROR,
  MANAGE_LOADING,
  MANAGE_TITLE,
  NO_EXPIRY,
  UNKNOWN_PERSON,
  coverageText,
  defaultConfirmText,
  grantedText,
  healthCheckLine,
  isoDay,
  loginLabelOf,
  missingScopesText,
  onDayBy,
  optionalScopesText,
  removeConfirmText,
  singlePointOfFailureText,
} from "./copy"
import DistributeDialog from "./DistributeDialog"
import { expiryWarningOf, healthChipOf } from "./meta-access.utils"
import MoveAssignmentsDialog from "./MoveAssignmentsDialog"

const ROW_BUTTON_CLS = "btn btn-ghost btn-xs"

function ExpiryValue({ iso, prefix }: { iso: string; prefix: string }) {
  const warning = expiryWarningOf(iso)
  return (
    <>
      <span>{`${prefix} ${isoDay(iso)}`}</span>
      {warning && <AccessChip tone={warning.tone}>{warning.label}</AccessChip>}
    </>
  )
}

interface LoginCardProps {
  user: MetaAccountUser
  busy: boolean
  canMove: boolean
  onReconnect: () => void
  onPause: (paused: boolean) => void
  onMakeDefault: () => void
  onDistribute: () => void
  onMove: () => void
  onRemove: () => void
}

function LoginCard({
  user,
  busy,
  canMove,
  onReconnect,
  onPause,
  onMakeDefault,
  onDistribute,
  onMove,
  onRemove,
}: LoginCardProps) {
  const label = loginLabelOf(user.connectedUser)
  const health = healthChipOf(user.health)
  const paused = user.pausedAt !== null
  return (
    <li
      aria-label={label}
      className="flex flex-col gap-2 rounded-md border border-basic-blue px-system-md py-system-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-white">{label}</span>
        {user.isDiscoveryDefault && (
          <AccessChip tone="blue">{LOGIN_CHIP.discoveryDefault}</AccessChip>
        )}
        {health && <AccessChip tone={health.tone}>{health.label}</AccessChip>}
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="text-system-gray-400">
          {LOGIN_ROW_LABEL.facebookAccount}
        </dt>
        <dd className="text-white">
          {user.connectedUser
            ? `${user.connectedUser.name ?? "unknown"} · ${user.connectedUser.id}`
            : UNKNOWN_PERSON}
        </dd>

        <dt className="text-system-gray-400">{LOGIN_ROW_LABEL.permissions}</dt>
        <dd className="flex flex-wrap items-center gap-2">
          <span>{grantedText(user.scopes.granted.length)}</span>
          {user.scopes.missingRequired.length > 0 && (
            <AccessChip tone="amber">
              {missingScopesText(user.scopes.missingRequired)}
            </AccessChip>
          )}
          {user.scopes.missingOptional.length > 0 && (
            <AccessChip tone="gray">
              {optionalScopesText(user.scopes.missingOptional)}
            </AccessChip>
          )}
        </dd>

        <dt className="text-system-gray-400">{LOGIN_ROW_LABEL.expires}</dt>
        <dd className="flex flex-wrap items-center gap-2">
          {user.expiresAt ? (
            <ExpiryValue iso={user.expiresAt} prefix="on" />
          ) : (
            <span>{NO_EXPIRY}</span>
          )}
        </dd>

        {user.dataAccessExpiresAt && (
          <>
            <dt className="text-system-gray-400">
              {LOGIN_ROW_LABEL.dataAccess}
            </dt>
            <dd className="flex flex-wrap items-center gap-2">
              <ExpiryValue iso={user.dataAccessExpiresAt} prefix="until" />
            </dd>
          </>
        )}

        <dt className="text-system-gray-400">{LOGIN_ROW_LABEL.assignments}</dt>
        <dd>{user.assignmentCount}</dd>

        <dt className="text-system-gray-400">{LOGIN_ROW_LABEL.coverage}</dt>
        <dd>
          <span title={COVERAGE_TITLE}>
            {coverageText(user.coverage.covered, user.coverage.total)}
          </span>
        </dd>

        <dt className="text-system-gray-400">{LOGIN_ROW_LABEL.connected}</dt>
        <dd>{onDayBy(user.setAt, user.setByName)}</dd>

        {user.lastError && (
          <>
            <dt className="text-system-gray-400">
              {LOGIN_ROW_LABEL.lastProblem}
            </dt>
            <dd className="text-system-gray-300">{user.lastError}</dd>
          </>
        )}
      </dl>

      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          disabled={busy}
          onClick={onReconnect}
          className={ROW_BUTTON_CLS}
        >
          {LOGIN_ACTION.reconnect}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onPause(!paused)}
          className={ROW_BUTTON_CLS}
        >
          {paused ? LOGIN_ACTION.resume : LOGIN_ACTION.pause}
        </button>
        {!user.isDiscoveryDefault && (
          <button
            type="button"
            disabled={busy}
            onClick={onMakeDefault}
            className={ROW_BUTTON_CLS}
          >
            {LOGIN_ACTION.makeDefault}
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={onDistribute}
          className={ROW_BUTTON_CLS}
        >
          {LOGIN_ACTION.distribute}
        </button>
        {canMove && (
          <button
            type="button"
            disabled={busy}
            onClick={onMove}
            className={ROW_BUTTON_CLS}
          >
            {LOGIN_ACTION.move}
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={onRemove}
          className={`${ROW_BUTTON_CLS} text-system-red-300`}
        >
          {LOGIN_ACTION.remove}
        </button>
      </div>
    </li>
  )
}

type Confirm = { kind: "remove" | "default"; user: MetaAccountUser }
type Child = { kind: "distribute" | "move"; user: MetaAccountUser }

export interface ManageAccountUsersDialogProps {
  onClose: () => void
}

/**
 * Super-admin dialog for every account user (APP-566 T2): the Marathon
 * people's Facebook logins, what each one is allowed and serves, and what to
 * do about them. Connect / Reconnect leave for Meta's dialog (OAuth only, no
 * paste) and come back through /authenticate-connector, which posts the code
 * to the API; the token never reaches the browser. Distribute and Move open
 * over the list, which steps aside while they are open.
 */
export default function ManageAccountUsersDialog({
  onClose,
}: ManageAccountUsersDialogProps) {
  const { data, isLoading, isError } = useGetMetaAccountUsersQuery()
  const [getOauthUrl, { isFetching: fetchingUrl }] =
    useLazyGetMetaAccountUserOauthUrlQuery()
  const [setPaused, { isLoading: pausing }] =
    useSetMetaAccountUserPausedMutation()
  const [removeUser, { isLoading: removing }] =
    useRemoveMetaAccountUserMutation()
  const [makeDefault, { isLoading: defaulting }] =
    useSetMetaAccountUserDiscoveryDefaultMutation()

  // Set once the browser has been told to leave for Meta and never cleared:
  // the navigation is in flight, so nothing here may fire again.
  const [leaving, setLeaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<Confirm | null>(null)
  const [child, setChild] = useState<Child | null>(null)

  const busy = fetchingUrl || leaving || pausing || removing || defaulting
  const users = data?.users ?? []
  // The open child dialog follows the list's latest copy of its login.
  const childUser = child
    ? (users.find(user => user.id === child.user.id) ?? child.user)
    : null

  const startLogin = async (credentialId?: string) => {
    setError(null)
    try {
      // Fetched on every click: the URL's state is single use.
      const { url } = await getOauthUrl(
        credentialId ? { credentialId } : {},
      ).unwrap()
      setLeaving(true)
      markMetaAccountLoginStarted()
      window.location.assign(url)
    } catch (err) {
      setError(getServerErrorMessage(err, CONNECT_START_FAILED))
    }
  }

  const run = async (action: () => Promise<unknown>) => {
    setError(null)
    try {
      await action()
      setConfirm(null)
    } catch (err) {
      setError(getServerErrorMessage(err, CHANGE_FAILED))
    }
  }

  const healthLine = data ? healthCheckLine(data.healthCheck) : null

  return (
    <>
      <Dialog
        isOpen={child === null}
        handleClose={onClose}
        closeOnClickOutside={!busy}
        width={640}
        classes={{ container: DIALOG_CONTAINER_CLS }}
      >
        <div>
          <h2 className="text-lg font-semibold text-white">{MANAGE_TITLE}</h2>
          <p className="mt-1 text-gray-400">{MANAGE_HELP}</p>
        </div>

        {healthLine && (
          <p
            data-tone={healthLine.warning ? "amber" : undefined}
            className={
              healthLine.warning ? "text-system-yellow-200" : "text-gray-400"
            }
          >
            {healthLine.text}
          </p>
        )}

        {data && data.singlePointOfFailure.length > 0 && (
          <p className="rounded-md border border-system-yellow-700 px-system-md py-system-sm text-system-yellow-200">
            {singlePointOfFailureText(
              data.singlePointOfFailure.map(({ companyName }) => companyName),
            )}
          </p>
        )}

        {isLoading && <p>{MANAGE_LOADING}</p>}
        {isError && <p className="text-states-error">{MANAGE_LOAD_ERROR}</p>}
        {data && users.length === 0 && <p>{MANAGE_EMPTY}</p>}

        {users.length > 0 && (
          <ul className="flex flex-col gap-2">
            {users.map(user => (
              <LoginCard
                key={user.id}
                user={user}
                busy={busy}
                canMove={users.length >= 2 && user.assignmentCount >= 1}
                onReconnect={() => void startLogin(user.id)}
                onPause={paused =>
                  void run(() => setPaused({ id: user.id, paused }).unwrap())
                }
                onMakeDefault={() => {
                  setError(null)
                  setConfirm({ kind: "default", user })
                }}
                onDistribute={() => setChild({ kind: "distribute", user })}
                onMove={() => setChild({ kind: "move", user })}
                onRemove={() => {
                  setError(null)
                  setConfirm({ kind: "remove", user })
                }}
              />
            ))}
          </ul>
        )}

        {confirm && (
          <div
            role="alertdialog"
            aria-label={
              confirm.kind === "remove"
                ? "Confirm removal"
                : "Confirm the discovery default"
            }
            className={
              confirm.kind === "remove"
                ? "flex flex-col gap-2 rounded-md border border-system-red-600 bg-system-red-900/30 px-system-md py-system-sm text-system-red-100"
                : "flex flex-col gap-2 rounded-md border border-basic-blue px-system-md py-system-sm text-white"
            }
          >
            <span>
              {confirm.kind === "remove"
                ? removeConfirmText(
                    confirm.user.connectedUser?.name ?? null,
                    confirm.user.isDiscoveryDefault,
                  )
                : defaultConfirmText(
                    confirm.user.connectedUser?.name ?? null,
                    confirm.user.health,
                  )}
            </span>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                disabled={busy}
                className="btn btn-ghost btn-xs"
              >
                {BUTTON.keepIt}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void run(() =>
                    confirm.kind === "remove"
                      ? removeUser({ id: confirm.user.id }).unwrap()
                      : makeDefault({ id: confirm.user.id }).unwrap(),
                  )
                }
                className={
                  confirm.kind === "remove"
                    ? "btn btn-error btn-xs"
                    : "btn btn-primary btn-xs"
                }
              >
                {(removing || defaulting) && (
                  <span className="loading loading-spinner loading-xs" />
                )}
                {confirm.kind === "remove"
                  ? LOGIN_ACTION.removeConfirm
                  : LOGIN_ACTION.makeDefault}
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2 rounded-md border border-basic-blue px-system-md py-system-sm">
          <p className="text-gray-300">{CONNECT_HELP}</p>
          <p className="text-system-yellow-200">{CONNECT_WARNING}</p>
          <div>
            <button
              type="button"
              onClick={() => void startLogin()}
              disabled={busy}
              className="btn btn-primary btn-sm"
            >
              {(fetchingUrl || leaving) && (
                <span className="loading loading-spinner loading-xs" />
              )}
              {CONNECT_LABEL}
            </button>
          </div>
        </div>

        {error && (
          <p role="alert" className="text-states-error">
            {error}
          </p>
        )}

        <div className="mt-1 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="btn btn-ghost btn-sm"
          >
            {BUTTON.close}
          </button>
        </div>
      </Dialog>

      {child?.kind === "distribute" && childUser && (
        <DistributeDialog
          credential={childUser}
          onClose={() => setChild(null)}
        />
      )}
      {child?.kind === "move" && childUser && (
        <MoveAssignmentsDialog
          from={childUser}
          users={users}
          onClose={() => setChild(null)}
        />
      )}
    </>
  )
}
